import {useCallback, useEffect, useState, type ReactElement} from 'react'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import {makeStyles} from '@material-ui/core'
import {AlertDialog} from '../../Common/Dialog'
import {backendGetRequest} from '../../../common/utils/requests'
import {renderMaintenanceInline, renderMaintenanceMarkdown} from './renderMaintenanceMarkdown'

const recheckIntervalMs = 30000
const dismissStorageKey = 'skyground.dismissedMaintenanceWindows'

type ActionButton = 'ok' | 'close'

type MaintenanceNotice = {
  id: number
  title: string
  description: string
  actionButtons: ActionButton[]
  startAt: string
  endAt: string
}

const useStyles = makeStyles({
  infoRowClassName: {
    marginTop: '12px',
    fontSize: '14px',
    lineHeight: '22px',
    '& p': {
      margin: '0 0 8px',
    },
    '& ul, & ol': {
      margin: '0 0 8px',
      paddingLeft: 20,
    },
    '& a': {
      color: 'inherit',
    },
  },
  buttonRow: {
    display: 'flex',
  },
  okButtonClassName: {
    flex: 1,
    textTransform: 'uppercase',
    marginTop: '12px',
  },
})

const readDismissed = (): number[] => {
  try {
    const raw = window.sessionStorage.getItem(dismissStorageKey)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : []
  } catch {
    return []
  }
}

const rememberDismissed = (ids: number[]) => {
  window.sessionStorage.setItem(dismissStorageKey, JSON.stringify(ids))
}

export function ScheduledMaintenancePopup(): ReactElement | null {
  const classes = useStyles()
  const [notices, setNotices] = useState<MaintenanceNotice[]>([])
  const [dismissedIds, setDismissedIds] = useState<number[]>(() => readDismissed())

  const loadNotices = useCallback(async () => {
    try {
      const response = await backendGetRequest('/api/v2/dashboard/maintenance-windows/active', true)
      if (!response.ok) {
        setNotices([])
        return
      }
      const body = await response.json()
      setNotices(Array.isArray(body.windows) ? body.windows : [])
    } catch {
      setNotices([])
    }
  }, [])

  useEffect(() => {
    loadNotices()
    const intervalId = window.setInterval(loadNotices, recheckIntervalMs)
    return () => window.clearInterval(intervalId)
  }, [loadNotices])

  const current = notices.find((notice) => !dismissedIds.includes(notice.id)) ?? null

  const dismiss = () => {
    if (!current) return
    const next = dismissedIds.includes(current.id) ? dismissedIds : [...dismissedIds, current.id]
    setDismissedIds(next)
    rememberDismissed(next)
  }

  if (!current) return null

  const buttons = current.actionButtons?.length ? current.actionButtons : ['ok']

  return (
    <AlertDialog
      title=""
      maxWidth="xs"
      open
      onClose={buttons.includes('close') ? dismiss : undefined}
    >
      <Grid container direction="column" justifyContent="space-between">
        <Grid item className={classes.infoRowClassName}>
          <strong dangerouslySetInnerHTML={{__html: renderMaintenanceInline(current.title)}} />
        </Grid>
        <Grid item
          className={classes.infoRowClassName}
          dangerouslySetInnerHTML={{__html: renderMaintenanceMarkdown(current.description)}}
        />
        <Grid item className={classes.buttonRow}>
          {buttons.includes('ok') && (
            <Button
              className={classes.okButtonClassName}
              style={buttons.includes('close') ? {marginRight: 8} : undefined}
              variant="contained"
              color="primary"
              onClick={dismiss}
            >
              OK
            </Button>
          )}
          {buttons.includes('close') && (
            <Button className={classes.okButtonClassName} variant="outlined" color="primary" onClick={dismiss}>
              Close
            </Button>
          )}
        </Grid>
      </Grid>
    </AlertDialog>
  )
}
